import React, { useCallback, useEffect } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { Loader } from "../../../organisms/common/loading";
import dayjs, {
  formatDatetime,
  formatUTCDateString,
} from "../../../../utils/date-utils";
import {
  AuthAuditLogContextProps,
  AuthAuditService,
} from "../../../../application/service/smart-apply/audit/auth-audit-service";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import { useMount } from "../../../../utils/custom-hooks";
import { SettingsTemplate } from "../../../template/settings/default-template";
import { SettingR } from "../route";
import {
  getOldestDateViewable,
  isAuthAuditLogOutsideTimestamp,
} from "../../../../application/domain/audit/auth-audit-log";
import { FeatureTitles } from "../../../../application/domain/faeture-titles";
import { useAuthContextV2 } from "../../../../auth-state";
import {
  DateRangeInput,
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { Pagination } from "@graffer-inc/console-design-kit";
import { AuthAuditLogsDownloadDialog } from "../../../organisms/settings/auth-audit/auth-audit-logs-download-dialog";

const handleStartFrom = (date: Date | undefined): Date => {
  return !date
    ? getOldestDateViewable()
    : isAuthAuditLogOutsideTimestamp(date)
      ? getOldestDateViewable()
      : date;
};

const handleEndTo = (date: Date | undefined): Date => {
  return !date
    ? dayjs().toDate()
    : isAuthAuditLogOutsideTimestamp(date)
      ? dayjs().toDate()
      : date;
};

const parseDateQuery = (from: string | undefined, to: string | undefined) => {
  const parsedFrom = from ? Date.parse(from!) : undefined;
  const parsedTo = to ? Date.parse(to!) : undefined;

  const dateFrom = parsedFrom ? new Date(parsedFrom) : undefined;
  const dateTo = parsedTo ? new Date(parsedTo) : undefined;

  return [handleStartFrom(dateFrom), handleEndTo(dateTo)];
};

const AuthAuditLogPage: React.FC<AuthAuditLogContextProps> = ({
  isLoading,
  auditLogs,
  currentPage,
  fromLimit,
  setFilterCondition,
  pushHistory,
  replaceHistory,
  totalPage,
  refreshAuditLogs,
  startExportAndDownloadAuthAuditLogs,
}) => {
  const { auth } = useAuthContextV2();
  const { page, from, to } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    from: string | undefined;
    to: string | undefined;
  };

  const [showExportAuthAuditLogsDiaglog, setShowExportAuthAuditLogsDiaglog] =
    React.useState(false);

  const [dateFrom, dateTo] = React.useMemo(() => {
    return parseDateQuery(from, to);
  }, [from, to]);

  useMount(() => {
    setFilterCondition({
      page: (page && parseInt(page, 10)) || 1,
      dateRange: {
        from: dateFrom,
        to: dateTo,
      },
    });
    replaceHistory();
  });

  useEffect(() => {
    if (!page && !from && !to) {
      // クエリパラメータ（認証ログの検索条件）が存在しない場合は何もしない
      return;
    }
    setFilterCondition({
      page: (page && parseInt(page, 10)) || 1,
      dateRange: {
        from: dateFrom,
        to: dateTo,
      },
    });
    refreshAuditLogs();
  }, [page, from, to]);

  const setFilterValue = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: Date | null;
      endDate: Date | null;
    }) => {
      setFilterCondition({
        page: 1,
        dateRange: {
          from: startDate ?? undefined,
          to: endDate ?? undefined,
        },
      });
      pushHistory();
    },
    [setFilterCondition, pushHistory],
  );

  if (!auth.hasPermissions("audit_logs.read")) {
    return <Redirect to={SettingR.MyAccount.link({})} />;
  }

  return (
    <SettingsTemplate
      breadcrumbs={[
        { to: SettingR.AuthAudit.link({}), title: FeatureTitles.AuthAuditLog },
      ]}
    >
      <h1 className="title">
        <i className="fa fa-tachometer" /> {FeatureTitles.AuthAuditLog}
      </h1>
      <div className="columns">
        <div className="column is-narrow">
          <DateRangeInput
            startDate={dateFrom}
            endDate={dateTo}
            setValue={setFilterValue}
            maxDate={dayjs().toDate()}
            minDate={getOldestDateViewable()}
          />
        </div>
        <div className="column">
          <div className="buttons">
            <SecondaryButton text={"表示"} onClick={refreshAuditLogs} />
            <PrimaryButton
              text={"CSVダウンロード"}
              onClick={() => setShowExportAuthAuditLogsDiaglog(true)}
            />
            {showExportAuthAuditLogsDiaglog && (
              <AuthAuditLogsDownloadDialog
                onClose={() => setShowExportAuthAuditLogsDiaglog(false)}
                exportAndDownloadAuthAuditLogs={
                  startExportAndDownloadAuthAuditLogs
                }
              />
            )}
          </div>
        </div>
      </div>
      <div className="c-appl-table">
        <Loader isLoading={isLoading}>
          <table className="table is-fullwidth">
            <thead>
              <tr>
                <th>ユーザーID</th>
                <th>ユーザー名</th>
                <th>イベント内容</th>
                <th>IPアドレス</th>
                <th>操作時間(UTC)</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((a) => (
                <tr key={a.occurAt + "_" + a.userId}>
                  <td>{a.userId}</td>
                  <td>{a.userName}</td>
                  <td>{a.description}</td>
                  <td>{a.ipAddress}</td>
                  <td>{formatUTCDateString(a.occurAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!!fromLimit &&
            currentPage >= totalPage &&
            dayjs(dateFrom).isBefore(dayjs(fromLimit)) && (
              <div className="notification is-warning is-light has-text-weight-bold">
                注：{formatDatetime(fromLimit)}
                より前の認証ログは表示されません。
              </div>
            )}
        </Loader>
        <div className="mt-3">
          <Pagination
            onPageClick={(p) => {
              setFilterCondition({
                page: p,
                dateRange: {
                  from: dateFrom,
                  to: dateTo,
                },
              });
              pushHistory();
            }}
            currentPage={currentPage}
            lastPage={totalPage}
          />
        </div>
      </div>
    </SettingsTemplate>
  );
};

export const AuthAuditLogContainer = connectPageService(
  AuthAuditLogPage,
  () => new AuthAuditService(),
);
